import {
  MATFILE_ENDLESS_TYPE,
  AUTOFILL_PATTERN,
} from 'pxc-editor-core/constants';
import Section from 'pxc-editor-core/entities/Section';
import {
  getAllLayers,
  autofillPagesWithStartSection,
  getSelectedSections,
  getSortedSectionsArray,
} from 'pxc-editor-core/helpers/schema';
import { getDictFirstKey } from 'pxc-editor-core/helpers/common';
import {
  AUTOFILL_MODE,
  COPY_MODE,
  ENDLESS_FIXED,
  SET_OTHER_CONTENT_STATE,
} from '@/constants';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { generateListByPattern } from '@/helpers/autofill';
import { autoFillAndCopySectionsThunk } from '@/store/actions/autofill-and-copy-sections';
import { createSnapshotThunk } from './undoRedo';
import { setSelectedPage, overridePages, overridePageAction } from './page';
import {
  changePageChildrenLength,
  handleShapeClick,
  setAutofillMatrixTemplate,
  switchSelectedSection,
} from './main';
import { setUnsafePageLength, setPageResizeType } from './pageResize';
import { paginationSetByPageItem } from './pagination';

const getLabelsList = data => {
  const {
    start,
    end,
    steps,
    prefix,
    suffix,
    isSettingsVisible,
    setting,
    loopsAmount,
    repeatsAmount,
    mode,
    copiesNo,
  } = data;

  switch (mode) {
    case AUTOFILL_MODE: {
      return generateListByPattern({
        autofillStart: start,
        autofillEnd: end,
        autofillSteps: steps,
        autofillPrefix: prefix,
        autofillSuffix: suffix,
        isAutofillSettingsVisible: isSettingsVisible,
        autofillSetting: setting,
        loopsAmount,
        repeatsAmount,
      });
    }
    case COPY_MODE: {
      // +1 - because filling includes first shield in handling
      // fill(null) - means copy full section content
      return Array(copiesNo + 1).fill(null);
    }
    default: {
      return [];
    }
  }
};

export const fillSections = settings => (dispatch, getState) => {
  const {
    core: { selWizard, sectionsSelection, pages, pagesSelection },
  } = getState();
  const { mode, pattern, isMatrix, copiesNo } = settings;

  if (isMatrix) {
    dispatch(createSnapshotThunk());

    const [currentPageUid] = pagesSelection;
    const currentPage = pages[currentPageUid];
    const selectedSections = getSelectedSections({
      sectionsSelection,
      page: currentPage,
    });
    const sections = getSortedSectionsArray(selectedSections);
    const [firstSection] = sections;

    switch (mode) {
      case COPY_MODE: {
        const content = Array(copiesNo).fill(firstSection);

        dispatch(
          setAutofillMatrixTemplate({
            autoFillMatrixTemplate: content,
            autoFillMatrixStyleTemplate: firstSection,
            fillMode: mode,
          })
        );
        dispatch(fillSectionByMatrixTemplateThunk());
        break;
      }
      case AUTOFILL_MODE: {
        const labelsList = getLabelsList(settings);

        dispatch(
          setAutofillMatrixTemplate({
            autoFillMatrixTemplate: labelsList,
            autoFillMatrixStyleTemplate: firstSection,
            fillMode: mode,
          })
        );
        dispatch(fillSectionByMatrixTemplateThunk());
        break;
      }
      default: {
        break;
      }
    }
  } else if (selWizard?.application_type === 'CABLE_FLAG') {
    dispatch(createSnapshotThunk());

    const labelsList = getLabelsList(settings);

    switch (mode) {
      case AUTOFILL_MODE: {
        dispatch(fillSectionsByFormSettings(labelsList, pattern));
        break;
      }
      case COPY_MODE: {
        dispatch(fillSectionsByFormSettings(labelsList, pattern));
        break;
      }
      default: {
        break;
      }
    }
  } else {
    dispatch(autoFillAndCopySectionsThunk(settings));
  }
};

const fillSectionsByFormSettings = (textList, pattern) => (
  dispatch,
  getState
) => {
  const state = getState();
  const {
    core: {
      pages,
      pagesOrder,
      sectionsSelection,
      includeOtherContent,
      selWizard,
    },
  } = state;

  const matfileTemplate = getBuildedMatfileTemplate();
  let selSectionId = getDictFirstKey(sectionsSelection);
  if (matfileTemplate && selSectionId) {
    const {
      store: newPages,
      order: newPagesOrder,
      lastElement: lastElement,
    } = autofillPagesWithStartSection(
      { store: pages, order: pagesOrder },
      selSectionId,
      textList.map(tli => ({ content: { text: tli } })),
      pattern,
      { matfile: matfileTemplate, includeOtherContent },
      state
    );

    dispatch(overridePages(newPages, newPagesOrder));
    dispatch(
      paginationSetByPageItem(newPagesOrder[newPagesOrder.length - 1], true)
    );

    if (selWizard?.application_type === 'CABLE_FLAG' && newPages) {
      Object.values(newPages).forEach(page => {
        dispatch(changePageChildrenLength(page.uid));
      });
    }

    if (lastElement.shield) {
      dispatch(handleShapeClick(lastElement.shield));
    }
    if (lastElement.section) {
      setTimeout(() => {
        dispatch(switchSelectedSection(lastElement.section));
      }, 10);
    }
  }
};

const selectFirstSection = () => (dispatch, getState) => {
  const {
    core: { pages, pagesOrder },
  } = getState();
  //// find first shield
  const firstPage = pages[pagesOrder[0]];
  let firstLayout = null;
  for (const layoutId in firstPage.layouts) {
    firstLayout = firstPage.layouts[layoutId];
    break;
  }
  let firstGroup = null;
  for (const groupId in firstLayout.groups) {
    firstGroup = firstLayout.groups[groupId];
    break;
  }
  let firstShield = null;
  for (const shieldId in firstGroup.shields) {
    firstShield = firstGroup.shields[shieldId];
    break;
  }
  // add selected section by simulation of first shield click
  dispatch(handleShapeClick(firstShield));
};

async function getSectionByLayers(selSectionId, pages) {
  return getAllLayers(selSectionId, pages);
}

async function setSectionWizardStyle(
  wizardData,
  selSectionId,
  pages,
  dispatch
) {
  await getSectionByLayers(selSectionId, pages).then(result => {
    if (result) {
      const _selSection = result.section;
      const selSection = Section.fromDict(_selSection);
      if (wizardData.text_style.hasOwnProperty('bold')) {
        selSection.setBold(wizardData.text_style.bold);
      }
      if (wizardData.text_style.hasOwnProperty('italic')) {
        selSection.setItalic(wizardData.text_style.italic);
      }
      if (wizardData.text_style.hasOwnProperty('text_height')) {
        selSection.setFontSize(wizardData.text_style.text_height);
      }
      if (wizardData.text_style.hasOwnProperty('font_name')) {
        selSection.setFont(wizardData.text_style.font_name);
      }
      if (wizardData.text_style.hasOwnProperty('horizontal_alignment')) {
        selSection.setHorizontalAlignment(
          wizardData.text_style.horizontal_alignment.toLowerCase()
        );
      }
      if (wizardData.text_style.hasOwnProperty('vertical_alignment')) {
        selSection.setVerticalAlignment(
          wizardData.text_style.vertical_alignment.toLowerCase()
        );
      }
    }
  });
}

// TODO: to many renders on start it from websocket. need more investigations
// last investigation result:
// each render happens on each dispatch call. looks like 'thunk' don't work on async(websocket) call
export const autofillProjectFromWizardData = wizardData => (
  dispatch,
  getState
) => {
  const {
    core: { pages, pagesOrder, pagesSelection },
  } = getState();
  const { type: matfileType } = getBuildedMatfileTemplate();
  const firstPageUid = pagesOrder[0];
  setTimeout(() => {
    if (!pagesSelection.length) {
      dispatch(setSelectedPage({ pageUid: firstPageUid }));
    }
    setTimeout(() => {
      if (wizardData.text_style) {
        // apply styles to selected section
        const {
          core: { sectionsSelection, pages },
        } = getState();
        let selSectionId = getDictFirstKey(sectionsSelection);
        if (!selSectionId) {
          dispatch(selectFirstSection());
          const {
            core: { sectionsSelection },
          } = getState();
          selSectionId = getDictFirstKey(sectionsSelection);
          setSectionWizardStyle(wizardData, selSectionId, pages, dispatch);
        } else {
          setSectionWizardStyle(wizardData, selSectionId, pages, dispatch);
        }
      }
      // set page length, so on autofill this value will be copied
      if (matfileType === MATFILE_ENDLESS_TYPE) {
        if (pages[firstPageUid].resizeType !== ENDLESS_FIXED) {
          dispatch(setPageResizeType(ENDLESS_FIXED, firstPageUid));
        }
        dispatch(setUnsafePageLength(wizardData.label_length, firstPageUid));
      }
      dispatch(
        fillSectionsByFormSettings(
          wizardData.labels_text,
          AUTOFILL_PATTERN.NEXT_CELL
        )
      );
    });
  });
};

export const setOtherContentState = checked => {
  return {
    type: SET_OTHER_CONTENT_STATE,
    payload: checked,
  };
};

const fillSectionByMatrixTemplateThunk = () => (dispatch, getState) => {
  const {
    core: {
      pages,
      pagesSelection,
      sectionsSelection,
      autoFillMatrixTemplate,
      autoFillMatrixStyleTemplate,
    },
  } = getState();
  const [currentPageUid] = pagesSelection;
  const currentPage = pages[currentPageUid];
  const selectedSections = getSortedSectionsArray(sectionsSelection);
  const [selectedSection] = selectedSections;
  const shieldSections = Object.values(currentPage.sections).reduce(
    (acc, item) => {
      if (item.shieldId === selectedSection.shieldId) {
        return { ...acc, [item.id]: item };
      }
      return acc;
    },
    {}
  );
  const matrixSections = getSortedSectionsArray(shieldSections);

  if (selectedSections.length > 1) {
    const updatedMatrixSections = selectedSections.reduce((acc, item) => {
      const content = autoFillMatrixTemplate.shift();
      if (content) {
        switch (typeof content) {
          case 'string': {
            return {
              ...acc,
              [item.id]: {
                ...item,
                ...Section.resetContent(item),
                ...Section.getContentSettings(autoFillMatrixStyleTemplate),
                text: content,
              },
            };
          }
          case 'object': {
            return {
              ...acc,
              [item.id]: {
                ...item,
                ...Section.getContentWithSettings(autoFillMatrixStyleTemplate),
              },
            };
          }
          default: {
            return acc;
          }
        }
      }
      return acc;
    }, {});
    const updatedPage = {
      ...currentPage,
      sections: {
        ...currentPage.sections,
        ...updatedMatrixSections,
      },
    };

    dispatch(
      overridePageAction({
        page: updatedPage,
      })
    );
  } else {
    const selectedSectionIndex = matrixSections.findIndex(
      item => item.id === selectedSection.id
    );
    const matrixSectionsToChange = matrixSections.slice(selectedSectionIndex);
    const updatedMatrixSections = matrixSectionsToChange.reduce((acc, item) => {
      const content = autoFillMatrixTemplate.shift();
      if (content) {
        switch (typeof content) {
          case 'string': {
            return {
              ...acc,
              [item.id]: {
                ...item,
                ...Section.resetContent(item),
                ...Section.getContentSettings(autoFillMatrixStyleTemplate),
                text: content,
              },
            };
          }
          case 'object': {
            return {
              ...acc,
              [item.id]: {
                ...item,
                ...Section.getContentWithSettings(autoFillMatrixStyleTemplate),
              },
            };
          }
          default: {
            return acc;
          }
        }
      }
      return acc;
    }, {});
    const updatedPage = {
      ...currentPage,
      sections: {
        ...currentPage.sections,
        ...updatedMatrixSections,
      },
    };

    dispatch(
      overridePageAction({
        page: updatedPage,
      })
    );
  }
};
